'use client';

import React, { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  User,
  Package,
  MapPin,
  Settings,
  // Star,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useBasePath } from '@/providers/BasePathProvider';
import { TabNavigation } from '@/components/TabNavigation';
import { AccountOverviewTab } from '@/components/AccountOverviewTab';
import { AccountOrdersTab } from '@/components/AccountOrdersTab';
import { AccountAddressesTab } from '@/components/AccountAddressesTab';
import { AccountSettingsTab } from '@/components/AccountSettingsTab';
// import { AccountReviewsTab } from '@/components/AccountReviewsTab';

const tabs = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'orders', label: 'Orders', icon: Package },
  // Reviews are temporarily hidden.
  // { id: 'reviews', label: 'My Reviews', icon: Star },
  // { id: 'payment', label: 'Payment', icon: CreditCard },
  { id: 'addresses', label: 'Addresses', icon: MapPin },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// TOGGLE THIS TO SWITCH DESIGNS
const USE_NEW_DESIGN = true;

/* ---------------------------------- */
/* Old Layout Component               */
/* ---------------------------------- */
function OldLayout({
  user,
  handleLogout,
  isLoggingOut,
  tabs,
  activeTab,
  setActiveTab,
  renderTabContent
}: any) {
  return (
    <div className="min-h-screen bg-linear-to-b from-[#F8FAFC] to-[#EBF4FB]">
      {/* Header */}
      <div className="">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-hsecondary">My Account</h1>
              <p className="text-sm text-hmuted">
                Manage your profile, orders, and preferences
              </p>
            </div>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="text-red-500 hover:text-red-700 text-sm font-medium"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      <div className="container mx-auto max-w-7xl">
        <div className="rounded-2xl shadow-xs">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------- */
/* New Layout Component               */
/* ---------------------------------- */
function NewLayout({
  user,
  handleLogout,
  isLoggingOut,
  tabs,
  activeTab,
  setActiveTab,
  renderTabContent
}: any) {
  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Premium Header - Reusing Shop Screen Design */}
      <div className="relative overflow-hidden bg-white pb-12 pt-8 md:pt-16 border-b border-gray-100">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-hprimary/5 rounded-[50%] blur-3xl -translate-y-1/2" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-hsecondary/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        </div>

        <div className="container mx-auto px-4 relative z-1">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-7xl mx-auto">
            <div className="text-center md:text-left space-y-4">
              {/* Badge */}
              <div className="flex justify-center md:justify-start">
                <span className="inline-flex items-center gap-2 rounded-full border border-hprimary/20 bg-hprimary/5 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-hprimary shadow-xs">
                  <User className="h-3.5 w-3.5" />
                  My Account
                </span>
              </div>

              <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
                Welcome back, <br className="hidden md:block" />
                <span className="bg-clip-text  text-hsecondary/80">
                  {user.firstname || 'Member'}
                </span>
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto md:mx-0 leading-relaxed">
                Manage your orders, prescriptions, and account settings all in one place.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Mobile Navigation (Horizontal Tabs) */}
          <div className="lg:hidden">
            <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
          </div>

          {/* Desktop Sidebar Navigation */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sticky top-24">
              <nav className="space-y-1">
                {tabs.map((tab: any) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 font-medium ${isActive
                        ? 'bg-hsecondary/80 text-white shadow-md shadow-hsecondary/20 translate-x-1'
                        : 'text-slate-600 hover:bg-hsecondary hover:text-white'
                        }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                      {tab.label}
                    </button>
                  );
                })}

                <div className="my-4 border-t border-gray-100" />

                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors font-medium"
                >
                  <Settings className="w-5 h-5 opacity-0" /> {/* Spacer icon for alignment if needed, using Settings for now or just padding */}
                  Log Out
                </button>
              </nav>
            </div>
          </aside>

          {/* Tab Content Area */}
          <main className="flex-1 min-w-0">
            {/* Wrappers to ensure consistent height matching the sidebar visually */}
            {/* <div className={`${activeTab === 'reviews'? '' : 'bg-white rounded-2xl shadow-sm border border-gray-100 '} min-h-[500px] ${activeTab === 'overview' ? 'p-0 bg-transparent shadow-none border-none' : 'p-0'}`}> */}
            <div className={`min-h-[500px] ${activeTab === 'overview' ? 'p-0 bg-transparent shadow-none border-none' : 'p-0'}`}>
              <div className={activeTab === 'overview' || activeTab === 'settings' ? '' : 'px-6'}>
                {renderTabContent()}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default function AccountPage() {
  const router = useRouter();
  const { user, logout, isLoading } = useAuth();
  const { buildPath } = useBasePath();
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // The account route stays mounted when only the query string changes, so keep
  // the visible tab in sync with navbar links such as ?tab=orders and /account.
  React.useEffect(() => {
    const requestedTab = searchParams.get('tab') || 'overview';
    const isKnownTab = tabs.some((tab) => tab.id === requestedTab);
    setActiveTab(isKnownTab ? requestedTab : 'overview');
  }, [searchParams]);

  // Listen for tab switch events from child components
  React.useEffect(() => {
    const handleTabSwitch = (event: CustomEvent) => {
      setActiveTab(event.detail);
    };
    window.addEventListener('switchTab', handleTabSwitch as EventListener);
    return () => {
      window.removeEventListener('switchTab', handleTabSwitch as EventListener);
    };
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      router.push(buildPath('/'));
    } catch (error: any) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (!user) {
    if (isLoading) return null;
    router.push(buildPath(`/login?redirect=${encodeURIComponent(typeof window !== 'undefined' ? window.location.pathname : '/account')}`));
    return null;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <AccountOverviewTab />;
      case 'orders':
        return <AccountOrdersTab />;
      // Reviews are temporarily hidden.
      // case 'reviews':
      //   return <AccountReviewsTab />;
      case 'addresses':
        return <AccountAddressesTab />;
      case 'settings':
        return <AccountSettingsTab />;
      default:
        return <AccountOverviewTab />;
    }
  };

  const layoutProps = {
    user,
    handleLogout,
    isLoggingOut,
    tabs,
    activeTab,
    setActiveTab,
    renderTabContent
  };

  if (USE_NEW_DESIGN) {
    return <NewLayout {...layoutProps} />;
  }

  return <OldLayout {...layoutProps} />;
}
